<template>
    <div>
        <!-- Live action buttons + Enqueue UI row -->
        <div class="kgr-row kgr-postfix kgr-postfix--between">
            <div class="kgr-postfix kgr-postfix--gutters kgr-postfix--bottom kgr-row">
                <div class="kgr-postfix__fixed">
                    <input
                        type="button"
                        :value="$t('Liste anzeigen')"
                        class="kgr-button kgr-button--accent"
                        @click="viewList"
                    />
                </div>
                <div class="kgr-postfix__fixed">
                    <input
                        type="button"
                        :value="$t('Excel-Download')"
                        class="kgr-button kgr-button--accent"
                        @click="downloadExcel"
                    />
                </div>
                <div class="kgr-postfix__fixed">
                    <input
                        type="button"
                        :value="$t('PDF-Download')"
                        class="kgr-button kgr-button--accent"
                        @click="downloadPdf"
                    />
                </div>
            </div>

            <!-- Enqueue UI -->
            <div class="kgr-postfix">
                <div
                    v-if="!inProgress"
                    class="animated fadeIn"
                >
                    <kgr-checkbox
                        v-model="notifyUser"
                        :label="$t('per E-Mail benachrichtigen')"
                        class="kgr-checkbox kgr-checkbox--inline"
                    />
                    <div class="kgr-postfix__fixed">
                        <kgr-button @click="enqueue()">
                            <span v-if="averageDuration">
                                {{ $t('Liste im Hintergrund vorberechnen (ca. {duration})', {'duration': averageDurationFormatted}) }}
                            </span>
                            <span
                                v-else
                                v-t="'Liste im Hintergrund vorberechnen'"
                            ></span>
                        </kgr-button>
                    </div>
                </div>
                <div
                    v-else
                    class="animated fadeIn"
                >
                    <div class="kgr-text">
                        {{ $t('Liste wird im Hintergrund vorberechnet...') }}
                    </div>
                    <div class="kgr-text">
                        {{ durationLeftFormatted() }}
                    </div>
                </div>
            </div>
        </div>

        <!-- Prefetched data alert -->
        <div
            v-if="showPrefetchedDataBlock"
            class="kgr-row animated fadeIn"
        >
            <div class="kgr-alert kgr-alert--warning">
                <div class="kgr-row">
                    {{ $t('Liste mit vorberechneten Daten verfügbar, Stand') }}
                    <span>{{ prefetchedDataFormatted }}</span>:
                </div>
                <div class="kgr-postfix kgr-postfix--gutters">
                    <div class="kgr-postfix__fixed">
                        <input
                            type="button"
                            :value="$t('Liste anzeigen')"
                            class="kgr-button kgr-button--accent"
                            @click="viewPrefetchedData"
                        />
                    </div>
                    <div class="kgr-postfix__fixed">
                        <input
                            type="button"
                            :value="$t('Excel-Download')"
                            class="kgr-button kgr-button--accent"
                            @click="downloadPrefetchedExcel"
                        />
                    </div>
                    <div class="kgr-postfix__fixed">
                        <input
                            type="button"
                            :value="$t('PDF-Download')"
                            class="kgr-button kgr-button--accent"
                            @click="downloadPrefetchedPdf"
                        />
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import Checkbox from '../common/form/checkbox/Checkbox';
import EventBus from '../../js/service/EventBus';
import KgrButton from '../common/form/button/KgrButton';
import ListPrefetchedDataRepository from '../../js/repository/ListPrefetchedDataRepository';
import moment from 'moment';

let activeController = null;

export default {
    components: {
        'kgr-checkbox': Checkbox,
        KgrButton,
    },
    props: {
        listId: {
            type: Number,
            required: false,
        },
        institutionType: {
            type: String,
            required: true,
            validator: (value) => ['federation', 'kindergarten', 'club'].includes(value),
        },
        institutionId: {
            type: Number,
            required: false,
        },
    },
    data() {
        return {
            enqueued: null,
            prefetchedData: null,
            prefetchedDataOutdated: false,
            enqueuing: false,
            enqueuingOverall: false,
            averageDuration: null,
            checkingIfEnqueued: false,
            notifyUser: false,
            originalConfigurationHash: null,
        };
    },
    computed: {
        inProgress() {
            return this.enqueuing || this.enqueuingOverall || !!this.enqueued;
        },
        averageDurationFormatted() {
            return moment.duration(this.averageDuration, 'seconds').humanize();
        },
        showPrefetchedDataBlock() {
            return this.prefetchedData && !this.prefetchedDataOutdated;
        },
        prefetchedDataFormatted() {
            if (!this.prefetchedData) {
                return '';
            }
            return moment(this.prefetchedData).format('DD.MM.YYYY HH:mm');
        },
        liveRoutes() {
            const routes = {
                federation: {
                    view: '/backend/lists/federationview/',
                    pdf: '/backend/lists/federation/pdf',
                    excel: '/backend/lists/federation/excel',
                },
                kindergarten: {
                    view: '/backend/lists/kindergartenview/',
                    pdf: '/backend/lists/kindergarten/pdf',
                    excel: '/backend/lists/kindergarten/excel',
                },
                club: {
                    view: `/backend/lists/clubview/${this.institutionId}`,
                    pdf: `/backend/lists/clubs/${this.institutionId}/pdf`,
                    excel: `/backend/lists/clubs/${this.institutionId}/excel`,
                },
            };
            return routes[this.institutionType] || {};
        },
        savedListRoutes() {
            if (!this.listId) {
                return null;
            }

            const routes = {
                federation: {
                    view: `/backend/lists/federation/${this.listId}/view`,
                    pdf: `/backend/lists/federation/${this.listId}/pdf`,
                    excel: `/backend/lists/federation/${this.listId}/excel`,
                },
                kindergarten: {
                    view: `/backend/lists/kindergarten/${this.listId}/view`,
                    pdf: `/backend/lists/kindergarten/${this.listId}/pdf`,
                    excel: `/backend/lists/kindergarten/${this.listId}/excel`,
                },
                club: {
                    view: `/backend/lists/clubs/${this.institutionId}/${this.listId}/view`,
                    pdf: `/backend/lists/clubs/${this.institutionId}/${this.listId}/pdf`,
                    excel: `/backend/lists/clubs/${this.institutionId}/${this.listId}/excel`,
                },
            };
            return routes[this.institutionType] || null;
        },
    },

    watch: {
        listId: {
            handler(id) {
                if (!id) {
                    return;
                }
                this.updateConfigurationHash();
                this.checkIfEnqueued();
            },
            immediate: true,
        },
    },
    mounted() {
        EventBus.$on('list:configuration-saved', this.updateConfigurationHash);
        setInterval(this.checkIfEnqueued, 3000);
        window?.initializeListConfigurator?.();

        setTimeout(() => {
            window?.initializeChosen?.();
        }, 500);
    },
    beforeUnmount() {
        EventBus.$off('list:configuration-saved');
    },

    methods: {
        durationLeftFormatted() {
            if (!this.enqueued || !this.averageDuration) {
                return null;
            }

            const timePast = moment().diff(this.enqueued, 'seconds');
            if (timePast >= this.averageDuration) {
                return null;
            }

            const timeLeft = moment.duration(this.averageDuration - timePast, 'seconds').humanize();

            return this.$t('Restdauer: circa {timeLeft}', {'timeLeft': timeLeft});
        },
        checkIfEnqueued() {
            if (this.checkingIfEnqueued || !this.listId) {
                return;
            }

            this.checkingIfEnqueued = true;
            ListPrefetchedDataRepository.checkIfEnqueued(this.listId)
                .then(data => {
                    if (!this.enqueuing) {
                        this.enqueuingOverall = false;
                    }

                    this.checkingIfEnqueued = false;
                    this.enqueued = data.enqueued;
                    this.prefetchedData = data.prefetchedData;
                    this.prefetchedDataOutdated = data.outdated;
                    this.averageDuration = data.averageDuration;
                })
                .catch(() => this.$notify.error('Fehler beim Überprüfen der Berechnung im Hintergrund'));
        },
        getPrefetchedDataUrl(suffix = '') {
            return `/backend/lists/${this.listId}/prefetchData${suffix}`;
        },
        viewPrefetchedData() {
            this.loadPrefetchedDataView();
        },
        async loadPrefetchedDataView() {
            const listEl = document.getElementById('list');
            if (!listEl) {
                return;
            }

            try {
                const response = await fetch(this.getPrefetchedDataUrl());
                const html = await response.text();
                listEl.innerHTML = html;
                window.showListActions?.();
            } catch {
                this.$notify.error('Fehler beim Laden der Liste');
            }
        },
        downloadPrefetchedExcel() {
            window.location.href = this.getPrefetchedDataUrl('/excel');
        },
        downloadPrefetchedPdf() {
            window.location.href = this.getPrefetchedDataUrl('/pdf');
        },

        // Live list actions (current configuration)
        async viewList() {
            if (!this.isInEditMode() && !this.hasUnsavedChanges()) {
                return this.viewSavedList();
            }

            return this.viewListPreview();
        },
        async viewListPreview() {
            const listEl = document.getElementById('list');
            if (!listEl || !this.liveRoutes.view) {
                return;
            }

            const formData = this.serializeListConfiguration();
            if (!formData) {
                return;
            }

            document.getElementById('tooltip_listLiveView')?.style.setProperty('display', 'none');
            window.showSpinner?.();

            const controller = new AbortController();
            activeController = controller;
            window.abortCurrentListRequest = () => controller.abort();

            try {
                const response = await fetch(this.liveRoutes.view, {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/x-www-form-urlencoded',
                    },
                    body: formData,
                    signal: controller.signal,
                });

                if (!response.ok) {
                    // Without this, a server error (e.g. a list too large to render, which 500s) was silently
                    // injected as its error body and never surfaced to the user - the spinner just stopped with
                    // no message. Mirrors viewSavedList().
                    throw new Error(`HTTP ${response.status}`);
                }

                const html = await response.text();
                listEl.innerHTML = html;
                window.showListActions?.();
            } catch (e) {
                if (e?.name !== 'AbortError') {
                    this.$notify.error('Fehler beim Laden der Liste');
                }
            } finally {
                if (activeController === controller) {
                    activeController = null;
                    window.abortCurrentListRequest = undefined;
                    window.hideSpinner?.();
                }
            }
        },
        async viewSavedList() {
            const listEl = document.getElementById('list');
            if (!listEl || !this.savedListRoutes?.view) {
                return;
            }

            document.getElementById('tooltip_listLiveView')?.style.setProperty('display', 'none');
            window.showSpinner?.();

            const controller = new AbortController();
            activeController = controller;
            window.abortCurrentListRequest = () => controller.abort();

            try {
                const response = await fetch(this.savedListRoutes.view, {
                    method: 'GET',
                    headers: {
                        'Accept': 'text/html',
                    },
                    signal: controller.signal,
                });

                if (!response.ok) {
                    throw new Error(`HTTP ${response.status}`);
                }

                const html = await response.text();
                listEl.innerHTML = html;
                window.showListActions?.();
            } catch (e) {
                if (e?.name !== 'AbortError') {
                    this.$notify.error('Fehler beim Laden der Liste');
                }
            } finally {
                if (activeController === controller) {
                    activeController = null;
                    window.abortCurrentListRequest = undefined;
                    window.hideSpinner?.();
                }
            }
        },
        downloadPdf() {
            if (!this.isInEditMode() && !this.hasUnsavedChanges() && this.savedListRoutes?.pdf) {
                window.open(this.savedListRoutes.pdf, '_blank');
                return;
            }
            this.submitFormToUrl(this.liveRoutes.pdf);
        },
        downloadExcel() {
            if (!this.isInEditMode() && !this.hasUnsavedChanges() && this.savedListRoutes?.excel) {
                window.open(this.savedListRoutes.excel, '_blank');
                return;
            }
            this.submitFormToUrl(this.liveRoutes.excel);
        },
        isInEditMode() {
            return document.getElementById('listEditMode') !== null;
        },
        hasUnsavedChanges() {
            if (!this.listId) {
                return true;
            }
            const currentHash = this.stringToHashCode(this.serializeListConfiguration() || '');
            return this.originalConfigurationHash !== currentHash;
        },
        submitFormToUrl(url) {
            const form = this.getElement_listConfiguration();
            if (!form || !url) {
                return;
            }

            const submitForm = document.createElement('form');
            submitForm.method = 'post';
            submitForm.action = url;

            new FormData(form).forEach((value, key) => {
                const input = document.createElement('input');
                input.type = 'hidden';
                input.name = key;
                input.value = value;
                submitForm.appendChild(input);
            });

            document.body.appendChild(submitForm);
            submitForm.submit();
            submitForm.remove();
        },

        enqueue() {
            const newConfigurationHash = this.stringToHashCode(this.serializeListConfiguration());
            if (!this.listId || this.originalConfigurationHash !== newConfigurationHash) {
                showAlert(this.$t('Nicht gespeicherte Änderungen'), this.$t('Bitte speichern Sie zunächst die Liste, um sie im Hintergrund berechnen zu können.'));
                return;
            }

            if (this.getListName().trim() === '') {
                showAlert(this.$t('Name fehlt'), this.$t('Bitte geben Sie der Liste zunächst einen Namen, um sie im Hintergrund berechnen zu können.'));
                return;
            }

            this.enqueuing = true;
            this.enqueuingOverall = true;
            ListPrefetchedDataRepository.enqueue(this.listId, this.notifyUser)
                .then(() => this.enqueuing = false)
                .catch(() => this.$notify.error('Liste konnte nicht für die Berechnung im Hintergrund vorgemerkt werden'));
        },
        stringToHashCode(string) {
            // @see https://stackoverflow.com/a/7616484
            let hash = 0, i, chr;
            if (string.length === 0) {
                return hash;
            }

            for (i = 0; i < string.length; i++) {
                chr = string.charCodeAt(i);
                hash = ((hash << 5) - hash) + chr;
                hash |= 0; // Convert to 32bit integer
            }

            return hash;
        },
        updateConfigurationHash() {
            const serialized = this.serializeListConfiguration();
            if (!serialized) {
                return false;
            }

            this.originalConfigurationHash = this.stringToHashCode(serialized);
        },
        serializeListConfiguration() {
            const form = this.getElement_listConfiguration();
            if (!form) {
                return null;
            }

            return new URLSearchParams(new FormData(form)).toString();
        },
        getListName() {
            const form = this.getElement_listConfiguration();
            if (!form) {
                return '';
            }

            const name = new FormData(form).get('name');
            return typeof name === 'string' ? name : '';
        },

        // External DOM element accessor (form is still external to this component)
        getElement_listConfiguration() {
            return document.getElementById('list_configuration');
        },
    }
}
</script>

<style scoped lang="scss" rel="stylesheet/scss">
</style>
